import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Card, CardBody, Col, Input, Row, Spinner } from "reactstrap";
import { AsyncButton } from "../../../common/buttons";
import { FONT_MONO } from "../wizardStyles";
import { installationProbeFingerprint } from "../shared/probeInstallationViaDashcam";
import {
  MAX_CHANNELS,
  fetchChannelThumbnails,
  manualScanBlocker,
  parseChannelRanges,
  scanInstallationChannels,
} from "../shared/scanInstallationChannels";

const probeKey = (dvr) => `probeResult:${dvr.id}`;
const DEFAULT_SCAN_RANGES = "1-32";

function buildRtspUrl(dvr) {
  const ip = dvr?.dvr_ipv4;
  const port = dvr?.dvr_rtsp_port;
  const suffix = dvr?.rtsp_url_suffix;
  if (!ip || !port) return null;
  const trimmedSuffix = (suffix ?? "").replace(/^\/+/, "");
  return `rtsp://${ip}:${port}/${trimmedSuffix}`;
}

// Dashcam reports h264/h265 in varied casings ("h.264", "H265"); normalize to the display form.
function formatCodec(encoding) {
  if (!encoding) return null;
  const normalized = String(encoding)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
  if (normalized === "h264") return "H.264";
  if (normalized === "h265") return "H.265";
  return String(encoding).toUpperCase();
}

// Current encoder config for one stream (main or sub) as display strings, or null when nothing is known.
function formatStreamSpec(stream) {
  if (!stream || typeof stream !== "object") return null;
  const spec = {
    resolution: stream.width && stream.height ? `${stream.width}×${stream.height}` : null,
    fps: stream.fps != null ? `${stream.fps} fps` : null,
    bitrate: stream.bitrate != null ? `${stream.bitrate} kbps` : null,
    codec: formatCodec(stream.video_encoding),
  };
  return Object.values(spec).some(Boolean) ? spec : null;
}

// Same display spec, but from persisted metadata (post-apply) where the probe is gone: stats are
// already stored in display form (resolution/codec strings) with raw numeric fps/bitrate.
function formatPersistedStreamSpec(stats) {
  if (!stats || typeof stats !== "object") return null;
  const spec = {
    resolution: stats.resolution || null,
    fps: stats.fps != null ? `${stats.fps} fps` : null,
    bitrate: stats.bitrate != null ? `${stats.bitrate} kbps` : null,
    codec: stats.codec || null,
  };
  return Object.values(spec).some(Boolean) ? spec : null;
}

// Per-channel stream stats for the apply payload → persisted on InstallationCamera.metadata (INS-260).
function channelMetadataFromRow(row) {
  if (!row) return null;
  const streamStats = (stream) => {
    if (!stream || typeof stream !== "object") return null;
    const stats = {};
    if (stream.width && stream.height) stats.resolution = `${stream.width}×${stream.height}`;
    if (stream.fps != null) stats.fps = stream.fps;
    if (stream.bitrate != null) stats.bitrate = stream.bitrate;
    const codec = formatCodec(stream.video_encoding);
    if (codec) stats.codec = codec;
    return Object.keys(stats).length ? stats : null;
  };
  const metadata = {};
  const main = streamStats(row.main);
  const sub = streamStats(row.sub);
  if (main) metadata.main = main;
  if (sub) metadata.sub = sub;
  return Object.keys(metadata).length ? metadata : null;
}

function ChannelThumbnail({ dataUri, channelNumber, loading, error, selected }) {
  return (
    <div
      className="d-flex align-items-center justify-content-center position-relative w-100 overflow-hidden bg-dark text-muted small rounded-top"
      style={{ aspectRatio: "16 / 9", fontFamily: FONT_MONO }}
    >
      {dataUri ? (
        <img
          src={dataUri}
          alt={`Channel ${channelNumber}`}
          className="w-100 h-100"
          style={{
            objectFit: "cover",
            filter: selected ? "none" : "grayscale(1) brightness(0.6)",
            transition: "filter 0.15s",
          }}
        />
      ) : loading ? (
        <Spinner size="sm" color="light" />
      ) : (
        <span>{error ? "No frame" : "No preview"}</span>
      )}
      <span
        className="position-absolute font-weight-bold bg-dark text-white rounded px-1"
        style={{ top: "0.4rem", left: "0.4rem", fontFamily: FONT_MONO }}
      >
        CH{String(channelNumber).padStart(2, "0")}
      </span>
    </div>
  );
}

function ChannelCard({
  channelNumber,
  name,
  onRename,
  selected,
  onToggle,
  dataUri,
  loading,
  error,
  streamSpecs,
  readOnly,
}) {
  const baseShadow = selected ? "0 0 0 2px var(--blue, #5e72e4)" : "0 0 0 1px rgba(0,0,0,0.08)";
  return (
    <Card
      style={{
        cursor: readOnly ? "default" : "pointer",
        boxShadow: baseShadow,
        opacity: !selected && !readOnly ? 0.65 : 1,
        transition: "box-shadow 0.15s, opacity 0.15s",
      }}
      onClick={(e) => {
        if (readOnly) return;
        // Don't toggle when the click started on a form control inside the card.
        if (e.target.tagName === "INPUT" || e.target.closest("input")) return;
        onToggle(channelNumber);
      }}
    >
      <ChannelThumbnail
        dataUri={dataUri}
        channelNumber={channelNumber}
        loading={loading}
        error={error}
        selected={selected}
      />
      <CardBody className="p-2">
        <div className="d-flex align-items-center" style={{ gap: "0.5rem" }}>
          <input
            type="checkbox"
            disabled={readOnly}
            checked={selected}
            onChange={() => onToggle(channelNumber)}
            onClick={(e) => e.stopPropagation()}
            className="m-0 flex-shrink-0 flex-grow-0"
          />
          <Input
            bsSize="sm"
            disabled={readOnly || !selected}
            value={name ?? ""}
            placeholder={`Channel ${channelNumber}`}
            onChange={(e) => onRename(channelNumber, e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
        <ChannelStreamSpecs main={streamSpecs?.main} sub={streamSpecs?.sub} />
        {error && <small className="text-warning d-block mt-1">{error}</small>}
      </CardBody>
    </Card>
  );
}

function ChannelGrid({ children }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
        gap: "0.75rem",
      }}
    >
      {children}
    </div>
  );
}

export function RecapRow({ label, value, mono }) {
  return (
    <div
      className="d-flex justify-content-between py-2"
      style={{ borderBottom: "1px solid rgba(0,0,0,0.06)", gap: "1rem" }}
    >
      <span className="text-muted small">{label}</span>
      <span
        className="text-right"
        style={{
          fontWeight: 600,
          fontSize: "0.85rem",
          fontFamily: mono ? "var(--font-mono, monospace)" : undefined,
          wordBreak: "break-all",
        }}
      >
        {value ?? "—"}
      </span>
    </div>
  );
}

function DvrRecap({ installation, dvr, selectedCount }) {
  const rtspUrl = buildRtspUrl(dvr);
  return (
    <>
      <RecapRow label="DVR login" value={dvr?.dvr_login} mono />
      <RecapRow label="DVR password" value={dvr?.dvr_password} mono />
      <RecapRow label="RTSP URL" value={rtspUrl} mono />
      {installation?.is_cloud && (
        <>
          <RecapRow label="External RTSP port" value={dvr?.dvr_external_rtsp_port} mono />
          <RecapRow label="External HTTP port" value={dvr?.dvr_external_http_port} mono />
        </>
      )}
      <RecapRow label="Channels selected" value={selectedCount} />
    </>
  );
}

function StreamSpecLine({ label, spec }) {
  if (!spec) return null;
  const parts = [spec.resolution, spec.fps, spec.bitrate, spec.codec].filter(Boolean);
  if (parts.length === 0) return null;
  return (
    <div className="d-flex small mt-1" style={{ gap: "0.4rem" }}>
      <span className="text-muted flex-shrink-0" style={{ width: "2.2rem" }}>
        {label}
      </span>
      <span style={{ fontFamily: FONT_MONO, wordBreak: "break-word" }}>{parts.join(" · ")}</span>
    </div>
  );
}

// Main + Sub encoder config (from the live probe or persisted metadata); renders nothing when unknown.
function ChannelStreamSpecs({ main, sub }) {
  if (!main && !sub) return null;
  return (
    <div className="mt-1">
      <StreamSpecLine label="Main" spec={main} />
      <StreamSpecLine label="Sub" spec={sub} />
    </div>
  );
}

// Channel grid + scan + recap for one DVR; reports its selection up via onSelectionChange.
export function DvrChannelSection({
  installation,
  dvr,
  index,
  draftFields,
  alreadyApplied,
  seededChannels,
  onSelectionChange,
}) {
  const fingerprint = installationProbeFingerprint(installation, dvr);
  const rawProbe = draftFields?.[probeKey(dvr)] ?? null;
  const probeResult = rawProbe && rawProbe.fingerprint === fingerprint ? rawProbe : null;
  const dashcamRows = probeResult?.source === "dashcam" ? probeResult.channels ?? [] : [];
  const dashcamRowByChannel = useMemo(() => {
    const map = {};
    dashcamRows.forEach((row) => {
      if (Number.isInteger(row?.channel)) map[row.channel] = row;
    });
    return map;
  }, [dashcamRows]);
  const dashcamNumbers = useMemo(() => dashcamRows.map((r) => r.channel).filter(Number.isInteger), [dashcamRows]);

  const [knownChannelNumbers, setKnownChannelNumbers] = useState(() => new Set());
  const [selectedNumbers, setSelectedNumbers] = useState(() => new Set());
  const [channelNames, setChannelNames] = useState({});
  const [thumbnails, setThumbnails] = useState({});
  const [thumbnailErrors, setThumbnailErrors] = useState({});
  const [thumbnailsLoading, setThumbnailsLoading] = useState(false);
  const [thumbnailMessage, setThumbnailMessage] = useState(null);

  const [scanRangesInput, setScanRangesInput] = useState(DEFAULT_SCAN_RANGES);
  const [scanError, setScanError] = useState(null);
  const [addChannelsInput, setAddChannelsInput] = useState("");

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Seed channel list + selection from dashcam result the first time it lands.
  const lastDashcamFingerprintRef = useRef(null);
  useEffect(() => {
    if (dashcamNumbers.length === 0) return;
    if (lastDashcamFingerprintRef.current === fingerprint) return;
    lastDashcamFingerprintRef.current = fingerprint;
    setKnownChannelNumbers((prev) => {
      const next = new Set(prev);
      dashcamNumbers.forEach((n) => next.add(n));
      return next;
    });
    setSelectedNumbers(new Set(dashcamNumbers));
  }, [dashcamNumbers, fingerprint]);

  // After apply + reload the probe results are gone; seed the grid once from the
  // channels saved on the DVR so the applied selection stays visible (read-only).
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    if (!alreadyApplied) return;
    if (dashcamNumbers.length > 0 || knownChannelNumbers.size > 0) return;
    if (!seededChannels || seededChannels.length === 0) return;
    seededRef.current = true;
    const numbers = seededChannels.map((ch) => ch.channel_number).filter(Number.isInteger);
    setKnownChannelNumbers(new Set(numbers));
    setSelectedNumbers(new Set(numbers));
    setChannelNames(
      Object.fromEntries(
        seededChannels
          .filter((ch) => Number.isInteger(ch.channel_number) && ch.channel_name)
          .map((ch) => [ch.channel_number, ch.channel_name])
      )
    );
  }, [alreadyApplied, dashcamNumbers.length, knownChannelNumbers.size, seededChannels]);

  const sortedKnown = useMemo(() => [...knownChannelNumbers].sort((a, b) => a - b), [knownChannelNumbers]);
  const sortedSelected = useMemo(() => [...selectedNumbers].sort((a, b) => a - b), [selectedNumbers]);

  // Post-apply the probe is gone; stats persisted on the DVR's channels (serialize_camera merges
  // metadata to the top level) keep the specs visible on the read-only grid.
  const seededStatsByChannel = useMemo(() => {
    const map = {};
    (seededChannels ?? []).forEach((ch) => {
      if (Number.isInteger(ch?.channel_number) && (ch.main || ch.sub)) {
        map[ch.channel_number] = { main: ch.main, sub: ch.sub };
      }
    });
    return map;
  }, [seededChannels]);

  // Report selection to the parent for the apply payload.
  useEffect(() => {
    const channels = sortedSelected.map((number) => {
      const name = (channelNames[number] ?? "").trim();
      const metadata = channelMetadataFromRow(dashcamRowByChannel[number]);
      return {
        channel_number: number,
        ...(name && { channel_name: name }),
        ...(metadata && { metadata }),
      };
    });
    onSelectionChange(dvr.id, channels);
  }, [sortedSelected, channelNames, dashcamRowByChannel, dvr.id, onSelectionChange]);

  // Missing frames mark channels as unreachable but don't block selection. `force` re-fetches.
  const loadThumbnails = useCallback(
    async (numbers, { force = false } = {}) => {
      const needed = force ? [...numbers] : numbers.filter((n) => !(n in thumbnails));
      if (needed.length === 0) return;
      const block = manualScanBlocker(installation, dvr);
      if (block) {
        setThumbnailMessage(`Stream previews unavailable: ${block}`);
        return;
      }
      setThumbnailsLoading(true);
      setThumbnailMessage(null);
      if (force) {
        setThumbnailErrors((prev) => {
          const next = { ...prev };
          needed.forEach((n) => {
            delete next[n];
          });
          return next;
        });
      }
      try {
        const fetched = await fetchChannelThumbnails({ installation, dvr, channelNumbers: needed });
        if (!mountedRef.current) return;
        setThumbnails((prev) => ({ ...prev, ...fetched }));
        const missing = needed.filter((n) => !(n in fetched));
        if (missing.length > 0) {
          setThumbnailErrors((prev) => {
            const next = { ...prev };
            missing.forEach((n) => {
              next[n] = "No frame returned.";
            });
            return next;
          });
        }
      } catch (err) {
        if (!mountedRef.current) return;
        setThumbnailMessage(err?.message ?? "Failed to load thumbnails.");
      } finally {
        if (mountedRef.current) setThumbnailsLoading(false);
      }
    },
    [installation, dvr, thumbnails]
  );

  // Serialized key fires on any membership change (adds + removals + transpositions).
  const knownChannelsKey = sortedKnown.join(",");
  useEffect(() => {
    if (sortedKnown.length === 0) return;
    if (alreadyApplied) return;
    loadThumbnails(sortedKnown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [knownChannelsKey, alreadyApplied]);

  const toggleSelection = (number) => {
    if (alreadyApplied) return;
    setSelectedNumbers((prev) => {
      const next = new Set(prev);
      if (next.has(number)) next.delete(number);
      else next.add(number);
      return next;
    });
  };

  const renameChannel = (number, name) => {
    setChannelNames((prev) => ({ ...prev, [number]: name }));
  };

  const handleSelectAll = () => {
    if (alreadyApplied) return;
    setSelectedNumbers(new Set(sortedKnown));
  };
  const handleDeselectAll = () => {
    if (alreadyApplied) return;
    setSelectedNumbers(new Set());
  };

  // Manual scan extends the grid past Dashcam's set (e.g. cameras offline at probe time).
  const parsedScanRanges = useMemo(() => parseChannelRanges(scanRangesInput), [scanRangesInput]);
  const tooManyScanChannels = Array.isArray(parsedScanRanges) && parsedScanRanges.length > MAX_CHANNELS;
  const scanRangesError = (() => {
    if (!scanRangesInput.trim()) return null;
    if (parsedScanRanges === null) return "Invalid ranges (e.g. 1-32 or 1,3,5).";
    if (tooManyScanChannels) return `Cannot scan more than ${MAX_CHANNELS} channels at once.`;
    return null;
  })();
  const manualBlock = manualScanBlocker(installation, dvr);
  const canScan =
    !alreadyApplied &&
    !manualBlock &&
    Array.isArray(parsedScanRanges) &&
    parsedScanRanges.length > 0 &&
    !tooManyScanChannels;

  const handleScan = useCallback(async () => {
    setScanError(null);
    try {
      const result = await scanInstallationChannels({ installation, dvr, channelNumbers: parsedScanRanges });
      if (!mountedRef.current) return;
      const numbersWithFrames = (result.screenshots ?? []).map((s) => s?.[0]).filter((n) => Number.isInteger(n));
      const thumbsFromScan = {};
      (result.screenshots ?? []).forEach((shot) => {
        const n = shot?.[0];
        const dataUri = shot?.[2];
        if (Number.isInteger(n) && typeof dataUri === "string" && dataUri.length > 0) {
          thumbsFromScan[n] = dataUri;
        }
      });
      setThumbnails((prev) => ({ ...prev, ...thumbsFromScan }));
      setKnownChannelNumbers((prev) => {
        const next = new Set(prev);
        parsedScanRanges.forEach((n) => next.add(n));
        return next;
      });
      setSelectedNumbers((prev) => {
        const next = new Set(prev);
        numbersWithFrames.forEach((n) => next.add(n));
        return next;
      });
    } catch (err) {
      if (mountedRef.current) setScanError(err?.message ?? "Scan failed.");
    }
  }, [installation, dvr, parsedScanRanges]);

  const addChannelsParse = parseChannelRanges(addChannelsInput);
  const canAddChannels = !alreadyApplied && Array.isArray(addChannelsParse) && addChannelsParse.length > 0;
  const handleAddChannels = () => {
    if (!canAddChannels) return;
    setKnownChannelNumbers((prev) => {
      const next = new Set(prev);
      addChannelsParse.forEach((n) => next.add(n));
      return next;
    });
    setSelectedNumbers((prev) => {
      const next = new Set(prev);
      addChannelsParse.forEach((n) => next.add(n));
      return next;
    });
    setAddChannelsInput("");
  };

  const dashcamPath = dashcamRows.length > 0;
  const hasKnownChannels = sortedKnown.length > 0;

  const autoScannedFingerprintRef = useRef(null);
  useEffect(() => {
    if (dashcamPath || hasKnownChannels || !canScan) return;
    if (autoScannedFingerprintRef.current === fingerprint) return;
    autoScannedFingerprintRef.current = fingerprint;
    handleScan();
  }, [dashcamPath, hasKnownChannels, canScan, fingerprint, handleScan]);

  return (
    <Card className="mb-3" style={{ border: "1px solid rgba(0,0,0,0.12)" }}>
      <CardBody>
        <h5 className="mb-3">DVR {index + 1}</h5>

        {!dashcamPath && (
          <Card className="mb-3">
            <CardBody>
              <small className="text-muted d-block mb-3">
                Dashcam wasn&apos;t used (or failed). Scan a range of channels to see which ones are reachable and grab
                a thumbnail.
              </small>
              <Row className="align-items-end">
                <Col md={4}>
                  <label className="text-muted d-block mb-1" style={{ fontSize: "0.75rem" }}>
                    Scan range
                  </label>
                  <Input
                    value={scanRangesInput}
                    placeholder={DEFAULT_SCAN_RANGES}
                    onChange={(e) => setScanRangesInput(e.target.value)}
                    disabled={alreadyApplied}
                  />
                  {scanRangesError && <small className="text-danger">{scanRangesError}</small>}
                </Col>
                <Col md={8}>
                  <div className="d-flex align-items-center" style={{ gap: "0.6rem" }}>
                    <AsyncButton color="primary" outline disabled={!canScan} onClick={handleScan}>
                      <i className="fas fa-binoculars mr-2" />
                      Scan channels
                    </AsyncButton>
                    {manualBlock && <small className="text-muted">{manualBlock}</small>}
                    {scanError && (
                      <small className="text-danger">
                        <i className="fas fa-times-circle mr-1" />
                        {scanError}
                      </small>
                    )}
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        )}

        <div className="d-flex align-items-center justify-content-between mb-3" style={{ gap: "0.75rem" }}>
          <div>
            <h6 className="mb-1">Channels</h6>
            <small className="text-muted d-block">
              {sortedSelected.length} of {sortedKnown.length} channel(s) selected for analysis
              {thumbnailsLoading && (
                <>
                  {" · "}
                  <Spinner size="sm" /> loading stream previews
                </>
              )}
            </small>
            <small className="text-muted d-block">
              Each tile is a snapshot of the camera&apos;s live stream — <em>Reload streams</em> re-fetches them, like a
              browser refresh.
            </small>
          </div>
          <div className="d-flex align-items-center" style={{ gap: "0.4rem" }}>
            <Button
              size="sm"
              outline
              color="secondary"
              disabled={alreadyApplied || !hasKnownChannels}
              onClick={handleSelectAll}
            >
              Select all
            </Button>
            <Button
              size="sm"
              outline
              color="secondary"
              disabled={alreadyApplied || sortedSelected.length === 0}
              onClick={handleDeselectAll}
            >
              Deselect all
            </Button>
            <AsyncButton
              size="sm"
              outline
              color="info"
              disabled={alreadyApplied || !hasKnownChannels}
              onClick={() => loadThumbnails(sortedKnown, { force: true })}
              title="Re-fetches a fresh snapshot from each camera stream, like refreshing a browser tab."
            >
              <i className="fas fa-sync-alt mr-1" />
              Reload streams
            </AsyncButton>
          </div>
        </div>

        {thumbnailMessage && (
          <Alert color="warning" className="small">
            {thumbnailMessage}
          </Alert>
        )}

        {hasKnownChannels ? (
          <ChannelGrid>
            {sortedKnown.map((number) => {
              const dashcamRow = dashcamRowByChannel[number];
              const seededStats = seededStatsByChannel[number];
              // Live probe wins; fall back to persisted stats once the probe is gone (post-apply).
              const streamSpecs = dashcamRow
                ? { main: formatStreamSpec(dashcamRow.main), sub: formatStreamSpec(dashcamRow.sub) }
                : seededStats
                ? { main: formatPersistedStreamSpec(seededStats.main), sub: formatPersistedStreamSpec(seededStats.sub) }
                : null;
              return (
                <ChannelCard
                  key={number}
                  channelNumber={number}
                  name={channelNames[number]}
                  onRename={renameChannel}
                  selected={selectedNumbers.has(number)}
                  onToggle={toggleSelection}
                  dataUri={thumbnails[number]}
                  loading={thumbnailsLoading && !(number in thumbnails)}
                  error={thumbnailErrors[number]}
                  streamSpecs={streamSpecs}
                  readOnly={alreadyApplied}
                />
              );
            })}
          </ChannelGrid>
        ) : (
          <Alert color="info" className="mb-0">
            No channels detected yet — run Dashcam on the RTSP step, or scan a range above to populate the grid.
          </Alert>
        )}

        <Row className="align-items-end mt-3">
          <Col md={5}>
            <label className="text-muted d-block mb-1" style={{ fontSize: "0.75rem" }}>
              Add channels (e.g. <code>17-24</code> or <code>3,5,9</code>)
            </label>
            <Input
              value={addChannelsInput}
              placeholder="Channel numbers to include manually"
              onChange={(e) => setAddChannelsInput(e.target.value)}
              disabled={alreadyApplied}
            />
          </Col>
          <Col md={3}>
            <Button color="secondary" outline disabled={!canAddChannels} onClick={handleAddChannels}>
              <i className="fas fa-plus mr-1" />
              Add to grid
            </Button>
          </Col>
          <Col md={4}>
            <small className="text-muted">
              Use this for cameras Dashcam couldn&apos;t reach (offline at probe time, non-standard channel numbers).
            </small>
          </Col>
        </Row>

        <div className="mt-3">
          <DvrRecap installation={installation} dvr={dvr} selectedCount={sortedSelected.length} />
        </div>
      </CardBody>
    </Card>
  );
}
